// client/src/components/auth//MyTestimonialsPage.js
import React, { useState, useEffect, useCallback } from 'react'; // Adicionado useCallback
import { useAuth } from '../../hooks/useAuth';
import { useTestimonials } from '../../hooks/useTestimonials';
import { Plus, MessageSquareQuote, Edit, Trash2, Loader2, Star, Save, Building, Globe, MoreVertical } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import FormModal from '../layout/FormModal';
import ActionsDrawer from '../common/ActionsDrawer';

const MyTestimonialsPage = () => {
    const { user } = useAuth();
    const { 
        testimonials, 
        loading, 
        error, 
        fetchMyTestimonials, 
        createPlatformTestimonial, 
        createAccountTestimonial, 
        updateMyTestimonial, 
        deleteMyTestimonial 
    } = useTestimonials();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentTestimonial, setCurrentTestimonial] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [testimonialType, setTestimonialType] = useState('platform'); // 'platform' ou 'account'
    const [selectedTestimonial, setSelectedTestimonial] = useState(null);

    // O useEffect chamará fetchMyTestimonials para carregar os depoimentos feitos pelo usuário logado
    useEffect(() => {
        fetchMyTestimonials();
    }, [fetchMyTestimonials]); // ✅ Adicionado fetchMyTestimonials como dependência

    const handleOpenModal = useCallback((testimonial = null) => {
        if (testimonial) {
            setCurrentTestimonial(testimonial);
            setIsEditing(true);
            setTestimonialType(testimonial.type || 'platform');
        } else {
            setCurrentTestimonial({
                authorName: user?.name || '',
                authorRole: '',
                companyName: user?.account?.name || user?.franquia?.nome || '',
                quote: '',
                rating: 5,
            });
            setIsEditing(false);
            setTestimonialType('platform');
        }
        setIsModalOpen(true);
        setSelectedTestimonial(null);
    }, [user]); // ✅ Adicionando user como dependência para que os dados iniciais do formulário sejam atualizados se o user mudar.

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        setCurrentTestimonial(null);
        setTestimonialType('platform');
    }, []);

    const handleChange = useCallback(e => setCurrentTestimonial(prev => ({ ...prev, [e.target.name]: e.target.value })), []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await updateMyTestimonial(currentTestimonial._id, currentTestimonial);
            } else {
                // ✅ Escolhe a função correta baseada no tipo para criar o depoimento
                if (testimonialType === 'platform') {
                    await createPlatformTestimonial(currentTestimonial);
                } else {
                    await createAccountTestimonial(currentTestimonial);
                }
            }
            fetchMyTestimonials(); // Recarrega a lista após criar/atualizar
            handleCloseModal();
        } catch (err) {
            // O hook useAlert já mostra o alerta, não é necessário duplicar aqui
        }
    }, [isEditing, currentTestimonial, testimonialType, updateMyTestimonial, createPlatformTestimonial, createAccountTestimonial, fetchMyTestimonials, handleCloseModal]);

    const handleDelete = useCallback(async (id) => {
        if (window.confirm('Tem certeza que deseja excluir seu depoimento?')) {
            await deleteMyTestimonial(id);
            fetchMyTestimonials(); // Recarrega a lista após a exclusão
        }
        setSelectedTestimonial(null);
    }, [deleteMyTestimonial, fetchMyTestimonials]);

    const renderStatusBadge = useCallback((status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
        };
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>{status}</span>;
    }, []);

    const renderTypeBadge = useCallback((type) => {
        const styles = {
            platform: 'bg-blue-100 text-blue-800',
            account: 'bg-purple-100 text-purple-800',
        };
        const labels = {
            platform: 'Plataforma',
            account: 'Conta'
        };
        return <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type] || 'bg-gray-100'}`}>{labels[type]}</span>;
    }, []);

    return (
        <div className="page-management-container">
            <PageHeader
                title="Meus Depoimentos"
                icon={<MessageSquareQuote className="icon-title" />}
                actions={
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                setTestimonialType('platform');
                                handleOpenModal();
                            }}
                            className="action-button action-button-primary flex items-center gap-2"
                        >
                            <Globe size={16} /> Avaliar Plataforma
                        </button>
                        <button
                            onClick={() => {
                                setTestimonialType('account');
                                handleOpenModal();
                            }}
                            className="action-button action-button-secondary flex items-center gap-2"
                        >
                            <Building size={16} /> Avaliar Conta
                        </button>
                    </div>
                }
            />

            <div className="content-panel mt-6">
                {loading ? (
                    <div className="text-center py-10">
                        <Loader2 className="animate-spin mx-auto" size={32} />
                    </div>
                ) : error ? (
                    <div className="text-center py-10 text-red-600">
                        <p>{error}</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {testimonials.length > 0 ? testimonials.map(t => (
                            <div key={t._id} className="border rounded-lg p-4 bg-white shadow-sm">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="flex items-center mb-2">
                                            {[...Array(t.rating)].map((_, i) => (
                                                <Star key={i} size={18} className="text-yellow-400 fill-current" />
                                            ))}
                                        </div>
                                        <p className="text-gray-700">"{t.quote}"</p>
                                        <div className="flex items-center mt-3 gap-3">
                                            {renderTypeBadge(t.type)}
                                            {renderStatusBadge(t.status)}
                                            <span className="text-xs text-gray-500">
                                                Enviado em: {new Date(t.createdAt).toLocaleDateString()}
                                            </span>
                                        </div>
                                    </div>
                                    <button onClick={() => setSelectedTestimonial(t)} className="action-icon-button text-gray-600" title="Ações">
                                        <MoreVertical size={18}/>
                                    </button>
                                </div>
                            </div>
                        )) : (
                            <p className="text-center py-10 text-gray-500">Você ainda não enviou nenhum depoimento.</p>
                        )}
                    </div>
                )}
            </div>

            {isModalOpen && (
                <FormModal title={isEditing ? 'Editar Depoimento' : `Enviar Novo Depoimento - ${testimonialType === 'platform' ? 'Plataforma' : 'Conta'}`} onClose={handleCloseModal}>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {!isEditing && (
                            <div className="form-group">
                                <label>Tipo de Depoimento</label>
                                <div className="flex gap-4">
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="testimonialType"
                                            value="platform"
                                            checked={testimonialType === 'platform'}
                                            onChange={() => setTestimonialType('platform')}
                                            className="form-radio"
                                        />
                                        <Globe size={16} />
                                        Sobre a Plataforma
                                    </label>
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="radio"
                                            name="testimonialType"
                                            value="account"
                                            checked={testimonialType === 'account'}
                                            onChange={() => setTestimonialType('account')}
                                            className="form-radio"
                                        />
                                        <Building size={16} />
                                        Sobre a Conta
                                    </label>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label>Seu Nome (como aparecerá no depoimento)</label>
                            <input
                                type="text"
                                name="authorName"
                                value={currentTestimonial?.authorName || ''} // Usar optional chaining para evitar erro se currentTestimonial for null
                                onChange={handleChange}
                                className="form-input"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Seu Cargo (opcional)</label>
                            <input
                                type="text"
                                name="authorRole"
                                value={currentTestimonial?.authorRole || ''} // Usar optional chaining
                                onChange={handleChange}
                                className="form-input"
                            />
                        </div>
                        <div className="form-group">
                            <label>Depoimento</label>
                            <textarea
                                name="quote"
                                value={currentTestimonial?.quote || ''} // Usar optional chaining
                                onChange={handleChange}
                                className="form-input"
                                rows="5"
                                required
                            />
                        </div>
                        <div className="form-group">
                            <label>Sua Avaliação</label>
                            <select
                                name="rating"
                                value={currentTestimonial?.rating || 5} // Usar optional chaining e default 5
                                onChange={handleChange}
                                className="form-input"
                            >
                                <option value="5">5 Estrelas</option>
                                <option value="4">4 Estrelas</option>
                                <option value="3">3 Estrelas</option>
                                <option value="2">2 Estrelas</option>
                                <option value="1">1 Estrela</option>
                            </select>
                        </div>
                        <div className="form-actions">
                            <button type="button" onClick={handleCloseModal} className="action-button action-button-secondary">
                                Cancelar
                            </button>
                            <button type="submit" className="action-button action-button-primary">
                                <Save size={18} /> {isEditing ? 'Salvar Alterações' : 'Enviar para Revisão'}
                            </button>
                        </div>
                    </form>
                </FormModal>
            )}

            <ActionsDrawer
                open={!!selectedTestimonial}
                onClose={() => setSelectedTestimonial(null)}
                title={selectedTestimonial?.authorName || 'Depoimento'}
                actions={selectedTestimonial ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleOpenModal(selectedTestimonial),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedTestimonial._id),
                        destructive: true,
                    },
                ] : []}
            />
        </div>
    );
};

export default MyTestimonialsPage;